import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiErrorDetail } from "@/lib/api";

const MODE = [["ganti_semua", "Ganti semua butir"], ["tambah_di_akhir", "Tambahkan di akhir"]];

// "Tempel dari Excel": teks tempelan + mode -> Periksa (server mengurai, TIDAK menyimpan) -> pratinjau per baris ->
// "Terapkan ke draf" (hanya bila tanpa galat; mengubah draf di layar, belum tersimpan sampai Simpan draf).
export default function SurveiTempelExcel({ open, versiId, butirSekarang, namaDimensi, onTutup, onTerapkan }) {
    const [teks, setTeks] = useState("");
    const [mode, setMode] = useState("ganti_semua");
    const [hasil, setHasil] = useState(null);
    const [sibuk, setSibuk] = useState(false);
    const [galatKirim, setGalatKirim] = useState("");

    const periksa = async () => {
        setSibuk(true); setGalatKirim(""); setHasil(null);
        try {
            const res = await api.post("/survei/versi-butir/impor-pratinjau", {
                versi_id: versiId, teks, mode,
                butir_sekarang: butirSekarang.map(({ kunci, dimensi_kode, teks: t, kalimat_negatif }) => ({ kunci, dimensi_kode, teks: t, kalimat_negatif })),
            });
            setHasil(res.data);
        } catch (e) { setGalatKirim(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setSibuk(false); }
    };
    const tutup = () => { setHasil(null); setGalatKirim(""); onTutup(); };
    const bolehTerapkan = hasil && hasil.galat.length === 0 && hasil.butir.length > 0;

    return (
        <Dialog open={open} onOpenChange={(o) => !o && tutup()}>
            <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Tempel dari Excel</DialogTitle>
                    <DialogDescription>
                        Di Excel, blok kolom Dimensi | Teks butir | Negatif | Boleh TB (dua kolom terakhir opsional; boleh termasuk baris judul), salin, lalu tempel di sini.
                    </DialogDescription>
                </DialogHeader>
                <Textarea value={teks} onChange={(e) => { setTeks(e.target.value); setHasil(null); }} rows={8}
                          className="font-mono text-xs" placeholder={"Dimensi\tTeks butir\tNegatif\tBoleh TB\nWC\tContoh pernyataan\ttidak\tya"}
                          data-testid="survei-tempel-teks" aria-label="Teks tempelan dari Excel" />
                <fieldset className="flex flex-wrap gap-4 text-sm">
                    <legend className="sr-only">Cara menerapkan</legend>
                    {MODE.map(([nilai, label]) => (
                        <label key={nilai} className="flex items-center gap-2">
                            <input type="radio" name="survei-tempel-mode" value={nilai} checked={mode === nilai}
                                   onChange={() => { setMode(nilai); setHasil(null); }} data-testid={`survei-tempel-${nilai}`} />
                            {label}
                        </label>
                    ))}
                </fieldset>
                {galatKirim && <p role="alert" className="text-sm text-rose-700">{galatKirim}</p>}
                {hasil && (
                    <div className="space-y-2" data-testid="survei-tempel-hasil">
                        <p className="text-sm text-slate-700">
                            {hasil.butir.length} butir terbaca · sesudah diterapkan: {hasil.jumlah_sesudah} butir
                            {hasil.galat.length ? ` · ${hasil.galat.length} galat` : ""}{hasil.peringatan.length ? ` · ${hasil.peringatan.length} peringatan` : ""}
                        </p>
                        {hasil.galat.length > 0 && (
                            <ul className="space-y-1 rounded-lg border border-rose-300 bg-rose-50 p-2 text-xs text-rose-800 dark:bg-rose-950 dark:text-rose-200" data-testid="survei-tempel-galat">
                                {hasil.galat.map((g, i) => <li key={i}>{g.pesan}</li>)}
                            </ul>
                        )}
                        {hasil.peringatan.length > 0 && (
                            <ul className="space-y-1 rounded-lg border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 dark:bg-amber-950 dark:text-amber-100" data-testid="survei-tempel-peringatan">
                                {hasil.peringatan.map((g, i) => <li key={i}>{g.pesan}</li>)}
                            </ul>
                        )}
                        {hasil.butir.length > 0 && (
                            <div className="max-h-64 overflow-auto rounded border border-slate-200">
                                <table className="compact w-full text-xs" data-testid="survei-tempel-tabel">
                                    <thead className="sticky top-0 bg-slate-100"><tr><th>Baris</th><th>Dimensi</th><th>Teks butir</th><th>Negatif</th><th>Boleh TB</th></tr></thead>
                                    <tbody>
                                        {hasil.butir.map((b) => (
                                            <tr key={b.baris} className="border-t border-slate-200 align-top">
                                                <td className="tabular-nums">{b.baris}</td>
                                                <td>{b.dimensi_kode} · {namaDimensi[b.dimensi_kode]}</td>
                                                <td className="whitespace-normal">{b.teks}</td>
                                                <td>{b.kalimat_negatif ? "Ya" : "–"}</td>
                                                <td>{b.boleh_tb === false ? "Tidak" : "Ya"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
                <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={tutup}>Batal</Button>
                    <Button variant="outline" onClick={periksa} disabled={!teks.trim() || sibuk} data-testid="survei-tempel-periksa">Periksa</Button>
                    <Button onClick={() => { onTerapkan(hasil, mode); setTeks(""); setHasil(null); }} disabled={!bolehTerapkan}
                            className="bg-[#00B9AD] text-[#0F2E30] hover:bg-[#60C0D0]" data-testid="survei-tempel-terapkan">
                        Terapkan ke draf
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
